"use client";

import Image from "next/image";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useBookingModal } from "@/contexts/BookingModalContext";
import { NEW_IMAGES } from "@/lib/images";
import { routes } from "@/lib/routes";

/** One image per card: parquet, synthetic, action, venue/social — paths exist under /public */
const CARD_IMAGES = [
  NEW_IMAGES.feature,
  NEW_IMAGES.synthetic[0],
  NEW_IMAGES.hero[1],
  NEW_IMAGES.scheduling,
] as const;

export default function ChooseCardsSection() {
  const { t } = useTranslation();
  const { openModal } = useBookingModal();

  const cards = [
    {
      title: t("chooseCards.card1Title"),
      desc: t("chooseCards.card1Desc"),
      cta: t("chooseCards.card1Cta"),
      onClick: openModal,
      href: null as string | null,
      image: CARD_IMAGES[0],
    },
    {
      title: t("chooseCards.card2Title"),
      desc: t("chooseCards.card2Desc"),
      cta: t("chooseCards.card2Cta"),
      onClick: openModal,
      href: null as string | null,
      image: CARD_IMAGES[1],
    },
    {
      title: t("chooseCards.card3Title"),
      desc: t("chooseCards.card3Desc"),
      cta: t("chooseCards.card3Cta"),
      onClick: null,
      href: routes.services.tournaments,
      image: CARD_IMAGES[2],
    },
    {
      title: t("chooseCards.card4Title"),
      desc: t("chooseCards.card4Desc"),
      cta: t("chooseCards.card4Cta"),
      onClick: null,
      href: routes.services.birthdays,
      image: CARD_IMAGES[3],
    },
  ];

  const ctaClass =
    "inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold uppercase tracking-wide text-white shadow-md transition hover:bg-[#1a3eb5] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white";

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-[#152a6b] via-primary-strong to-[#0f2458] py-14 sm:py-16 md:py-24">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
        aria-hidden
      />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" aria-hidden />

      <div className="relative mx-auto w-full max-w-6xl px-4 md:px-6">
        <div className="mx-auto max-w-3xl text-center">
          <h2
            className="text-balance text-2xl font-black tracking-tight text-white sm:text-3xl md:text-4xl"
            suppressHydrationWarning
          >
            {t("chooseCards.title")}
          </h2>
          <p
            className="mx-auto mt-4 max-w-2xl text-pretty text-sm leading-relaxed text-white/85 sm:text-base"
            suppressHydrationWarning
          >
            {t("chooseCards.subtitle")}
          </p>
        </div>

        <div className="mt-10 grid gap-6 sm:mt-12 sm:grid-cols-2 lg:mt-14 lg:grid-cols-4 lg:gap-6">
          {cards.map((card, index) => (
            <div
              key={card.title}
              className="group flex h-full flex-col overflow-hidden rounded-2xl border border-white/15 bg-white shadow-[0_20px_50px_-12px_rgba(0,0,0,0.35)] ring-1 ring-black/5 transition duration-300 hover:-translate-y-1 hover:shadow-[0_28px_60px_-12px_rgba(0,0,0,0.45)] sm:rounded-3xl"
            >
              <div className="relative aspect-[16/10] w-full shrink-0 overflow-hidden bg-slate-200">
                <Image
                  src={card.image}
                  alt=""
                  fill
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                  className="object-cover object-[50%_22%] transition duration-700 ease-out group-hover:scale-[1.04]"
                  loading={index < 2 ? "eager" : "lazy"}
                  quality={80}
                />
                <div
                  className="absolute inset-0 bg-gradient-to-t from-black/35 via-black/5 to-transparent"
                  aria-hidden
                />
              </div>

              <div className="flex flex-1 flex-col p-5 sm:p-6">
                <div className="min-h-0 flex-1">
                  <h3
                    className="text-lg font-bold leading-snug tracking-tight text-slate-900 sm:text-[1.125rem]"
                    suppressHydrationWarning
                  >
                    {card.title}
                  </h3>
                  <p
                    className="mt-3 text-sm leading-relaxed text-slate-600"
                    suppressHydrationWarning
                  >
                    {card.desc}
                  </p>
                </div>

                <div className="mt-6 shrink-0 sm:mt-7">
                  {card.href ? (
                    <Link href={card.href} className={ctaClass}>
                      <span suppressHydrationWarning>{card.cta}</span>
                      <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
                    </Link>
                  ) : (
                    <button type="button" onClick={card.onClick ?? undefined} className={ctaClass}>
                      <span suppressHydrationWarning>{card.cta}</span>
                      <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
